import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, BarChart3, ExternalLink } from "lucide-react";
import SEO from "../components/SEO";

const HEADLINE_STATS = [
  { num: "400,000+", label: "Children currently in the U.S. foster care system." },
  { num: "20,000", label: "Young adults \u201Cage out\u201D of the system every year without a permanent family." },
  { num: "50%", label: "Of youth who age out will experience homelessness within two years." },
  { num: "Every 2 min.", label: "A child enters foster care in America." },
];

const DEMOGRAPHICS = [
  { label: "Average age in care", value: "8.4 years" },
  { label: "Median time in care", value: "15.5 months" },
  { label: "Children re-entering care", value: "~26,000/year" },
  { label: "Placed with relatives (kinship)", value: "35%" },
  { label: "Placed in non-relative foster families", value: "45%" },
  { label: "Placed in group homes / institutions", value: "9%" },
];

const OUTCOMES = [
  { stat: "60%", label: "of child sex trafficking victims have a history of foster care or group homes", source: "National Center for Missing & Exploited Children, 2024", href: "https://www.missingkids.org/blog/2024/child-sex-trafficking-survivors" },
  { stat: "2×", label: "more likely to suffer from PTSD than U.S. war veterans (former foster youth)", source: "Casey Family Programs — Northwest Alumni Study", href: "https://www.casey.org/northwest-alumni-study/" },
  { stat: "<3%", label: "of former foster youth earn a college degree by age 25 (vs. 36% of general population)", source: "National Foster Youth Institute", href: "https://nfyi.org/issues/education/" },
  { stat: "1 in 4", label: "former foster youth experience PTSD-level symptoms within 4 years of leaving care", source: "Casey Family Programs", href: "https://www.casey.org/" },
  { stat: "70%", label: "of girls who age out of care become pregnant by age 21", source: "Annie E. Casey Foundation · Midwest Study", href: "https://www.aecf.org/" },
  { stat: "~25%", label: "of foster youth become incarcerated within 2 years of leaving the system", source: "Juvenile Law Center, 2023", href: "https://jlc.org/issues/extended-foster-care" },
];

export default function Statistics() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  // FAQPage schema — answers based on the 4 verbatim headline stats on this page.
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "How many children are currently in the U.S. foster care system?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "More than 400,000 children are currently in the U.S. foster care system. Data sourced from AFCARS (U.S. Dept. of Health and Human Services), The Casey Foundation, and the National Foster Youth Institute.",
        },
      },
      {
        "@type": "Question",
        "name": "How often does a child enter the U.S. foster care system?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Every 2 minutes, a child enters foster care in America. That works out to over 200,000 new entries every year.",
        },
      },
      {
        "@type": "Question",
        "name": "How many foster youth age out of the system each year without a family?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Approximately 20,000 young adults age out of the U.S. foster care system every year without a permanent family or legal guardian.",
        },
      },
      {
        "@type": "Question",
        "name": "What percentage of youth who age out of foster care experience homelessness?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Roughly 50% of youth who age out of foster care will experience homelessness within two years of leaving the system.",
        },
      },
    ],
  };

  return (
    <div data-testid="statistics-page">
      <SEO
        path="/statistics"
        title="Foster Care Statistics 2026 | Facts & Data on U.S. Foster Care"
        description="Up-to-date foster care statistics for 2026 — how many children are in foster care, aging out data, racial disparities, outcomes, and what the numbers mean for advocates and policymakers."
        jsonLd={faqSchema}
      />

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-fc-border bg-fc-bg">
        <div className="absolute inset-0 opacity-[0.07] pointer-events-none" aria-hidden="true">
          <div className="absolute inset-0" style={{
            backgroundImage: "repeating-linear-gradient(90deg, currentColor 0 1px, transparent 1px 80px), repeating-linear-gradient(0deg, currentColor 0 1px, transparent 1px 80px)",
            color: "var(--fc-ink)"
          }} />
        </div>
        <div className="container-xl relative pt-20 pb-16 md:pt-28 md:pb-20">
          <div className="overline text-fc-accent mb-6 flex items-center gap-2" data-testid="stats-eyebrow">
            <BarChart3 size={14} /> The Data · 365 Days a Year
          </div>
          <h1 className="font-display text-5xl md:text-7xl lg:text-[96px] leading-[0.92] tracking-tight text-fc-ink max-w-5xl">
            Foster Care, <span className="italic text-fc-accent">By the Numbers.</span>
          </h1>
          <p className="mt-8 text-lg md:text-xl max-w-3xl text-fc-muted leading-relaxed">
            Shining a light on the reality of the system through data.
          </p>
        </div>
      </section>

      {/* HEADLINE STATS */}
      <section className="bg-fc-ink text-fc-bg" data-testid="headline-stats">
        <div className="container-xl py-20">
          <div className="overline mb-8 opacity-70">Headline Figures · United States</div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0 border-t border-l border-fc-bg/15">
            {HEADLINE_STATS.map((s, i) => (
              <div key={i} className="p-8 border-r border-b border-fc-bg/15 flex flex-col">
                <div className="font-display text-4xl md:text-5xl text-fc-accent leading-none mb-4">{s.num}</div>
                <div className="text-sm md:text-base leading-snug opacity-90 flex-1">{s.label}</div>
              </div>
            ))}
          </div>
          <p className="mt-8 text-xs md:text-sm opacity-60 leading-relaxed max-w-4xl" data-testid="headline-citation">
            Data sourced from <a href="https://www.acf.hhs.gov/cb/research-data-technology/statistics-research/afcars" target="_blank" rel="noopener noreferrer" className="link-ul">AFCARS</a> (U.S. Dept. of Health and Human Services), <a href="https://www.casey.org/" target="_blank" rel="noopener noreferrer" className="link-ul">The Casey Foundation</a>, and the <a href="https://nfyi.org/" target="_blank" rel="noopener noreferrer" className="link-ul">National Foster Youth Institute</a>.
          </p>
        </div>
      </section>

      {/* DEMOGRAPHICS BREAKDOWN */}
      <section className="container-xl py-20 md:py-28" data-testid="demographics">
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="overline text-fc-muted mb-4">Demographics · Care Profile</div>
            <h2 className="font-display text-4xl md:text-5xl leading-tight">
              Who is in care — <span className="italic text-fc-accent">and for how long.</span>
            </h2>
            <p className="mt-6 text-fc-muted leading-relaxed max-w-md">
              Foster care is not one experience. It's an infant placed at birth, a teenager in a fifth home, a sibling group held together by a grandparent, a youth turning 18 with nowhere to go. The averages below outline the shape of the system.
            </p>
            <p className="mt-4 text-xs text-fc-muted opacity-60">
              Source: AFCARS FY 2023 Report (Children's Bureau, 2024).
            </p>
          </div>
          <div className="md:col-span-7">
            <div className="border-t border-l border-fc-border">
              {DEMOGRAPHICS.map((d) => (
                <div key={d.label} className="grid grid-cols-12 border-r border-b border-fc-border">
                  <div className="col-span-7 p-5 text-sm text-fc-ink">{d.label}</div>
                  <div className="col-span-5 p-5 text-right font-display text-2xl text-fc-accent border-l border-fc-border">{d.value}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* AGING-OUT OUTCOMES */}
      <section className="bg-fc-surface border-y border-fc-border" data-testid="outcomes">
        <div className="container-xl py-20 md:py-28">
          <div className="max-w-3xl mb-12">
            <div className="overline text-fc-accent mb-4">After the System · The Outcomes We Don't Talk About</div>
            <h2 className="font-display text-4xl md:text-6xl leading-[0.95]">
              What happens when no one shows up.
            </h2>
            <p className="mt-6 text-fc-muted leading-relaxed">
              For the 20,000 youth who exit foster care each year without a permanent family, the outcomes are not theoretical. They are measurable — and devastating. These figures are why <em className="text-fc-accent not-italic">365</em> exists: because awareness for one month a year is not enough.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {OUTCOMES.map((o, i) => (
              <div key={i} className="border border-fc-border bg-fc-bg p-7 flex flex-col">
                <div className="font-display text-5xl md:text-6xl text-fc-accent leading-none mb-5">{o.stat}</div>
                <div className="text-sm md:text-base text-fc-ink leading-snug flex-1 mb-5">{o.label}</div>
                <a href={o.href} target="_blank" rel="noreferrer noopener" className="overline text-fc-muted hover:text-fc-ink inline-flex items-center gap-1.5 transition-colors">
                  {o.source} <ExternalLink size={11} />
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* METHODOLOGY */}
      <section className="container-xl py-20" data-testid="methodology">
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <div className="overline text-fc-muted mb-3">Methodology</div>
            <h3 className="font-display text-3xl leading-tight">How these numbers were gathered.</h3>
          </div>
          <div className="md:col-span-8 text-fc-muted leading-relaxed space-y-4 text-sm md:text-base">
            <p>
              All headline figures are drawn from the U.S. Department of Health & Human Services' <strong className="text-fc-ink">Adoption and Foster Care Analysis and Reporting System (AFCARS)</strong>, FY 2023 report (released 2024). AFCARS is the federal mandatory reporting system covering every state and territory.
            </p>
            <p>
              Outcome statistics — aging-out, education, trafficking links, mental-health outcomes — are drawn from <strong className="text-fc-ink">Casey Family Programs</strong> (Northwest Alumni Study and Midwest Evaluation), the <strong className="text-fc-ink">National Foster Youth Institute</strong>, the <strong className="text-fc-ink">Annie E. Casey Foundation</strong>'s Kids Count Data Center, the <strong className="text-fc-ink">National Center for Missing & Exploited Children</strong>, and the <strong className="text-fc-ink">Juvenile Law Center</strong>.
            </p>
            <p>
              Figures are updated annually as new federal data is released. If you spot a source that is out of date, please <Link to="/contact" className="link-ul text-fc-ink">flag it</Link>.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-fc-ink text-fc-bg" data-testid="stats-cta">
        <div className="container-xl py-20 md:py-28 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="overline opacity-70 mb-4">From Data to Action</div>
            <h2 className="font-display text-4xl md:text-6xl leading-tight">
              Numbers don't <span className="italic text-fc-accent">change lives.</span>
            </h2>
            <p className="mt-6 max-w-md opacity-80 leading-relaxed">
              People do. Become a foster parent, a mentor, a respite caregiver, a court-appointed advocate, a donor, or a story-sharer. Every role matters.
            </p>
          </div>
          <div className="flex flex-wrap gap-4">
            <Link to="/get-involved" className="btn-primary" style={{background:"#F9F9F6", color:"#121212", borderColor:"#F9F9F6"}} data-testid="stats-cta-involved">
              Ways to Help <ArrowUpRight size={14} />
            </Link>
            <Link to="/am-i-ready-to-foster" className="btn-outline" style={{borderColor:"#F9F9F6", color:"#F9F9F6"}} data-testid="stats-cta-quiz">
              Am I Ready to Foster?
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
